import { Landmark, FileText, BadgeCheck, Download } from 'lucide-react';
import { toast } from 'react-toastify';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import Tabs from '../../../components/Tabs/Tabs';
import Table, { StatusPill } from '../../../components/Table/Table';

import usePayrollEntity from '../../../utils/usePayrollEntity';
import { fmtRs, fmtMoney } from '../../../utils/payrollFormat';
import { toIsoDate } from '../../../utils/dateFormat';

const ICON = { size: 15 };

const TABLE_PROPS = {
  search: true,
  searchLabel: 'Search',
  pageSize: 10,
  scrollClassName: '',
};

const statusColumn = {
  key: 'status',
  label: 'Status',
  align: 'center',
  render: (r) => <StatusPill label={r.status} />,
};

/* ── Bank Transfer ───────────────────────────────────────────────────────── */
const BankTransferTab = () => {
  const { list, loading } = usePayrollEntity('bankTransfer');

  return (
    <Table
      {...TABLE_PROPS}
      title="Bank Transfer"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['bank']}
      searchPlaceholder="Search by bank..."
      emptyMessage="No bank transfers found"
      columns={[
        { key: 'bank', label: 'Bank', grow: true, className: 'font-semibold text-gray-900' },
        { key: 'employeeCount', label: 'Employees', align: 'right', className: 'text-gray-600' },
        {
          key: 'amount',
          label: 'Amount (Rs)',
          align: 'right',
          className: 'font-semibold text-gray-900',
          render: (r) => fmtMoney(r.amount),
        },
        statusColumn,
      ]}
    />
  );
};

/* ── Bank File Generation ────────────────────────────────────────────────── */
const BankFileGenerationTab = () => {
  const { list, loading } = usePayrollEntity('bankFileGeneration');
  const notify = (name) => toast.info(`Downloading ${name} (demo).`, { autoClose: 2000 });

  return (
    <Table
      {...TABLE_PROPS}
      title="Bank File Generation"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['bank', 'fileName']}
      searchPlaceholder="Search bank files..."
      emptyMessage="No bank files found"
      columns={[
        { key: 'bank', label: 'Bank', className: 'font-semibold text-gray-900' },
        { key: 'fileName', label: 'File Name', grow: true, className: 'text-gray-700' },
        {
          key: 'generatedOn',
          label: 'Generated On',
          align: 'center',
          className: 'text-gray-600 tabular-nums',
          render: (r) => toIsoDate(r.generatedOn),
        },
        statusColumn,
        {
          key: 'actions',
          label: 'Actions',
          align: 'center',
          sortable: false,
          render: (r) => (
            <button
              type="button"
              onClick={() => notify(r.fileName)}
              className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] font-semibold
                text-primary-700 hover:bg-primary-50 transition-colors"
            >
              <Download size={14} />
              Download
            </button>
          ),
        },
      ]}
    />
  );
};

/* ── Salary Payment Confirmation ─────────────────────────────────────────── */
const PaymentConfirmationTab = () => {
  const { list, loading } = usePayrollEntity('paymentConfirmation');

  return (
    <Table
      {...TABLE_PROPS}
      title="Salary Payment Confirmation"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['empNo', 'name', 'bank']}
      searchPlaceholder="Search payment confirmations..."
      emptyMessage="No payment confirmations found"
      columns={[
        {
          key: 'name',
          label: 'Employee',
          grow: true,
          className: 'font-semibold text-gray-900',
          render: (r) => `${r.name} (${r.empNo})`,
        },
        { key: 'bank', label: 'Bank', className: 'text-gray-600' },
        { key: 'accountNumber', label: 'Account No.', className: 'text-gray-600 tabular-nums' },
        {
          key: 'amount',
          label: 'Amount',
          align: 'right',
          className: 'font-semibold text-gray-900',
          render: (r) => fmtRs(r.amount),
        },
        statusColumn,
      ]}
    />
  );
};

/* ── Page ────────────────────────────────────────────────────────────────── */
const Payments = () => {
  const tabs = [
    { key: 'transfer', label: 'Bank Transfer', icon: <Landmark {...ICON} />, content: <BankTransferTab /> },
    { key: 'file', label: 'Bank File Generation', icon: <FileText {...ICON} />, content: <BankFileGenerationTab /> },
    { key: 'confirmation', label: 'Salary Payment Confirmation', icon: <BadgeCheck {...ICON} />, content: <PaymentConfirmationTab /> },
  ];

  return (
    <div className="p-6 space-y-4">
      <Breadcrumb
        trail={['Payroll', 'Payments']}
        title="Payments"
        subtitle="Generate bank transfer files and confirm salary payments."
      />
      <Tabs tabs={tabs} defaultTab="transfer" />
    </div>
  );
};

export default Payments;